import {
  GQBiChartKind,
  GQBiFilterOperator,
  GQBiSavedViewVisibility,
  GQElectricityType,
} from '../generated/graphql';
import { YMInterval } from '../utils/YearMonth';
import assertNever from '../utils/assertNever';
import isNotNullish from '../utils/isNotNullish';
import { BiSavedViewPartialType } from './types';

export enum TemplateIcon {
  calendar,
  city,
  flash,
  friends,
  money,
  cleanPower,
  calculator,
  auto,
}

export type WatershedSavedViewType = Omit<
  BiSavedViewPartialType,
  'stableId' | 'ownerId' | 'isDraft'
> & {
  templateId: string;
  owner: {
    displayName: string;
  };
  icon: TemplateIcon;
  description: string;
  intervalType: SavedViewTemplateIntervalType;
};

const SHARED_FIELDS = {
  order: null,
  filters: [],
  timeIntervals: [],
  searchTerm: null,
  queryContext: null,
  editVisibility: GQBiSavedViewVisibility.Org,
  viewVisibility: GQBiSavedViewVisibility.Org,
  chartKind: GQBiChartKind.TimeSeries,
  owner: {
    displayName: 'Watershed',
  },
};

export enum SavedViewTemplateIntervalType {
  Full,
  MostRecent,
  TimeComparison,
}

/**
 * Computes the intervals for a saved view template based on the type of interval and the max interval.
 *
 * @param maxInterval - The maximum interval to consider
 * @param intervalType - The type of interval to compute
 * @param fiscalYearStartMonth - The fiscal year start month, if applicable. If null, it is ignored.
 * @returns An array of intervals that the saved view template should use.
 */
export function computeSavedViewTemplateInterval(
  maxInterval: YMInterval,
  intervalType: SavedViewTemplateIntervalType,
  fiscalYearStartMonth: number | null
): Array<YMInterval> {
  const ranges = (
    fiscalYearStartMonth
      ? maxInterval
          .allFiscalYears(fiscalYearStartMonth)
          .map((year) => YMInterval.forFiscalYear(year, fiscalYearStartMonth))
      : maxInterval.allPresentYears().map((year) => {
          const intervalInRange =
            YMInterval.forYear(year).intersect(maxInterval);
          if (intervalInRange) {
            return intervalInRange;
          }
        })
  ).filter(isNotNullish);
  switch (intervalType) {
    case SavedViewTemplateIntervalType.MostRecent:
      return ranges.slice(ranges.length - 1, ranges.length);
    case SavedViewTemplateIntervalType.TimeComparison:
      return ranges.length < 2
        ? ranges
        : ranges.slice(ranges.length - 2, ranges.length);
    case SavedViewTemplateIntervalType.Full:
      return [maxInterval];
    default:
      assertNever(intervalType);
  }
}

export const WATERSHED_SAVED_VIEW_TEMPLATES: Array<WatershedSavedViewType> = [
  {
    ...SHARED_FIELDS,
    intervalType: SavedViewTemplateIntervalType.MostRecent,
    icon: TemplateIcon.cleanPower,
    templateId: 'ghg_scopes_absolute',
    name: 'Scopes 1 - 3 (absolute emissions)',
    description: 'Absolute emissions, grouped by GHGP Scope and Category',
    dimensions: [
      'ghgScope',
      'ghgCategoryId',
      'categoryId',
      'subcategoryId',
      'description',
    ],
    normalizedMeasures: [{ baseMeasure: 'tco2eQuantity' }],
  },
  {
    ...SHARED_FIELDS,
    icon: TemplateIcon.calculator,
    intervalType: SavedViewTemplateIntervalType.MostRecent,
    templateId: 'percentage_of_total_footprint',
    name: 'Scope 1 - 3 (% of total footprint)',
    description:
      'Emissions as a percentage of your total footprint, grouped by GHGP Scope and Category',
    dimensions: [
      'ghgScope',
      'ghgCategoryId',
      'categoryId',
      'subcategoryId',
      'description',
    ],
    normalizedMeasures: [
      {
        baseMeasure: 'tco2eQuantity',
        normalizingQuery: {
          measure: 'tco2eQuantity',
          dimensions: [],
          filters: [],
        },
      },
    ],
  },
  {
    ...SHARED_FIELDS,
    icon: TemplateIcon.calendar,
    intervalType: SavedViewTemplateIntervalType.TimeComparison,
    templateId: 'annual_comparison',
    name: 'Annual comparison',
    description: 'Year-over-year comparison of your footprint',
    dimensions: [
      'ghgScope',
      'ghgCategoryId',
      'categoryId',
      'subcategoryId',
      'description',
    ],
    normalizedMeasures: [
      {
        baseMeasure: 'tco2eQuantity',
      },
    ],
  },
  {
    ...SHARED_FIELDS,
    icon: TemplateIcon.auto,
    templateId: `scope_3_ghg_categories`,
    intervalType: SavedViewTemplateIntervalType.MostRecent,
    name: 'Scope 3 by GHG Category',
    description: 'Scope 3 emissions, grouped by GHGP Category',
    filters: [
      {
        dimension: 'ghgScope',
        operator: GQBiFilterOperator.In,
        value: ['Scope 3'],
      },
    ],
    dimensions: ['ghgCategoryId', 'categoryId', 'subcategoryId', 'description'],
    normalizedMeasures: [{ baseMeasure: 'tco2eQuantity' }],
  },
  {
    ...SHARED_FIELDS,
    icon: TemplateIcon.city,
    intervalType: SavedViewTemplateIntervalType.MostRecent,
    templateId: `energy_use_by_energy_kind`,
    name: 'Energy use by energy kind',
    description: 'Energy use within the organization, grouped by energy kind',
    filters: [
      {
        dimension: 'ghgScope',
        operator: GQBiFilterOperator.In,
        value: ['Scope 1', 'Scope 2'],
      },
    ],
    dimensions: ['energyKind', 'description'],
    normalizedMeasures: [{ baseMeasure: 'mwhConsumed' }],
  },
  {
    ...SHARED_FIELDS,
    icon: TemplateIcon.flash,
    intervalType: SavedViewTemplateIntervalType.MostRecent,
    templateId: `scope_2_by_electricity_type`,
    name: 'Scope 2 by electricity type',
    description: 'Scope 2 emissions, grouped by electricity type (clean, grid)',
    dimensions: [
      'electricityType',
      'ghgCategoryId',
      'categoryId',
      'subcategoryId',
    ],
    normalizedMeasures: [{ baseMeasure: 'mwhConsumed' }],
    filters: [
      {
        dimension: 'ghgScope',
        operator: GQBiFilterOperator.In,
        value: ['Scope 2'],
      },
      {
        dimension: 'energyKind',
        operator: GQBiFilterOperator.In,
        value: ['electricity'],
      },
      {
        dimension: 'electricityType',
        operator: GQBiFilterOperator.In,
        value: [GQElectricityType.Grid, GQElectricityType.Clean],
      },
    ],
  },
  {
    ...SHARED_FIELDS,
    icon: TemplateIcon.money,
    intervalType: SavedViewTemplateIntervalType.MostRecent,
    templateId: `emissions_by_revenue`,
    name: 'Emissions by revenue',
    description: 'Emissions relative to your organization’s revenue',
    dimensions: [
      'ghgScope',
      'ghgCategoryId',
      'categoryId',
      'subcategoryId',
      'description',
    ],
    normalizedMeasures: [
      {
        baseMeasure: 'tco2eQuantity',
        normalizingQuery: {
          measure: 'revenue',
          computedDimensionType: 'NumeratorIntersection',
          filters: [],
        },
      },
    ],
  },
  {
    ...SHARED_FIELDS,
    icon: TemplateIcon.friends,
    intervalType: SavedViewTemplateIntervalType.MostRecent,
    templateId: `headcount_intensity_by_scope`,
    name: 'Emissions by headcount',
    description: 'Emissions relative to your organization’s headcount',
    dimensions: [
      'ghgScope',
      'ghgCategoryId',
      'categoryId',
      'subcategoryId',
      'description',
    ],
    normalizedMeasures: [
      {
        baseMeasure: 'tco2eQuantity',
        normalizingQuery: {
          measure: 'headcount',
          computedDimensionType: 'NumeratorIntersection',
          filters: [],
        },
      },
    ],
  },
];
